import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import LeadSuccessStatsModal from './LeadSuccessStatsModal';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function LeadsTab({ userRole, users, currentUserId, isDarkMode, onSwitchToComplaints }) {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const isAdmin = userRole === 'admin' || userRole === 'callcentrum';

  const [selectedUser, setSelectedUser] = useState(
    isAdmin ? 'all' : currentUserId
  );
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLeadSuccessStatsModal, setShowLeadSuccessStatsModal] = useState(false);
  const [configBatches, setConfigBatches] = useState([]); // { batchName, value }

  const availableUsers = isAdmin
    ? (users || [])
    : [];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/callcenter/config?userId=${currentUserId || ''}`);
        if (res.ok) {
          const data = await res.json();
          setConfigBatches((data.buttons || []).filter(b => b.isActive !== false));
        }
      } catch (e) {
        console.error('Chyba při načítání callcenter config v LeadsTab:', e);
      }
    };
    fetchConfig();
  }, [currentUserId]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      let url = `${API_URL}/leads-assignments?month=${month}&year=${year}&userId=${currentUserId || ''}`;
      if (selectedUser && selectedUser !== 'all') url += `&filterUserId=${selectedUser}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setLeads(data);
      } else {
        setLeads([]);
      }
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [selectedMonth, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build price map from config
  const batchPriceMap = {};
  configBatches.forEach(b => { if (b.batchName) batchPriceMap[b.batchName] = b.value || 0; });

  // Get all batch names: from config + from actual data (for historical)
  const configBatchNames = configBatches.map(b => b.batchName).filter(Boolean);
  const dataBatchNames = [...new Set(leads.map(l => l.batchName).filter(Boolean))];
  const allBatchNames = [...new Set([...configBatchNames, ...dataBatchNames])];

  const th = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const td = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`;
  const tdMuted = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const card = `rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`;

  const closedCount = leads.filter(l => l.forecast === '100').length;
  const totalCount = leads.length;
  const successRate = totalCount > 0 ? Math.round((closedCount / totalCount) * 100) : null;

  const batchStats = allBatchNames.map(batchName => {
    const batchLeads = leads.filter(l => l.batchName === batchName);
    const closed = batchLeads.filter(l => l.forecast === '100').length;
    const total = batchLeads.length;
    const rate = total > 0 ? Math.round((closed / total) * 100) : null;
    return { batchName, closed, total, rate };
  });

  let grandTotal = 0;

  return (
    <div>
      {/* Filtry */}
      <div className={`flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div>
          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
        </div>

        {isAdmin && (
          <div>
            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Uživatel</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="all">Všichni</option>
              {availableUsers.map(u => (
                <option key={u._id} value={u._id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}

        {totalCount > 0 && (
          <div className={`ml-2 flex flex-wrap gap-2 items-center`}>
            {batchStats.map(s => s.rate !== null && (
              <div key={s.batchName} className={`px-3 py-2 rounded-lg border flex items-center gap-2 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{s.batchName.replace('Lead ', '').replace(' - nadiktované podklady', '').replace(' - vyúčtování k dispozici', '')}:</span>
                <span className={`text-sm font-bold ${s.rate >= 50 ? 'text-green-500' : s.rate >= 25 ? 'text-yellow-500' : 'text-red-500'}`}>{s.rate} %</span>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>({s.closed} / {s.total})</span>
              </div>
            ))}
            <div className={`px-3 py-2 rounded-lg border flex items-center gap-2 ${isDarkMode ? 'bg-gray-700 border-gray-500' : 'bg-gray-50 border-gray-300'}`}>
              <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Celkem:</span>
              <span className={`text-sm font-bold ${successRate >= 50 ? 'text-green-500' : successRate >= 25 ? 'text-yellow-500' : 'text-red-500'}`}>{successRate} %</span>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>({closedCount} / {totalCount})</span>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám…</div>
      ) : (
        <div className="space-y-6">
          {allBatchNames.map(batchName => {
            const batchLeads = leads.filter(l => l.batchName === batchName);
            const price = batchPriceMap[batchName] || 0;
            const batchTotal = batchLeads.reduce((sum, l) => sum + (l.price || price || 0), 0);
            grandTotal += batchTotal;

            return (
              <div key={batchName} className={card}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📋 {batchName}
                  </h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                    {batchLeads.length} leadů · {batchTotal.toLocaleString('cs-CZ')} Kč
                  </span>
                </div>

                {batchLeads.length === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Žádné leady v tomto měsíci.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {isAdmin && <th className={th}>Uživatel</th>}
                          <th className={th}>Název zákazníka</th>
                          <th className={th}>IČO</th>
                          <th className={th}>Stav</th>
                          <th className={th}>Forecast</th>
                          <th className={th}>BMSL</th>
                          <th className={th}>Cena</th>
                          <th className={th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchLeads.map((lead, idx) => (
                          <tr
                            key={lead.transferId || idx}
                            className={`border-b transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-600' : 'border-gray-100 hover:bg-gray-50'}`}
                          >
                            {isAdmin && <td className={tdMuted}>{lead.toUserName || '—'}</td>}
                            <td className={`${td} font-medium`}>{lead.companyName || '—'}</td>
                            <td className={tdMuted}>{lead.companyIco || '—'}</td>
                            <td className={tdMuted}>{lead.status || '—'}</td>
                            <td className={tdMuted}>{lead.forecast || '—'}</td>
                            <td className={tdMuted}>{lead.bmsl || '—'}</td>
                            <td className={`${td} font-semibold`}>{price.toLocaleString('cs-CZ')} Kč</td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => onSwitchToComplaints && onSwitchToComplaints(lead.companyName, lead.companyIco)}
                                className={`text-xs px-2 py-1 rounded font-medium transition-colors ${isDarkMode ? 'bg-red-800 hover:bg-red-700 text-red-200' : 'bg-red-100 hover:bg-red-200 text-red-700'}`}
                              >
                                ⚠️ Reklamovat
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                          {isAdmin && <td className={td}></td>}
                          <td className={`${td} font-bold`}>Celkem</td>
                          <td className={td}></td>
                          <td className={td}></td>
                          <td className={td}></td>
                          <td className={td}></td>
                          <td className={`${td} font-bold`}>{batchTotal.toLocaleString('cs-CZ')} Kč</td>
                          <td className={td}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Grand total */}
          {leads.length > 0 && (
            <div className={`flex justify-end p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Celkem za měsíc: <span className="text-blue-500">{grandTotal.toLocaleString('cs-CZ')} Kč</span>
                  {' '}({leads.length} leadů)
                </span>
                <button
                  onClick={() => setShowLeadSuccessStatsModal(true)}
                  className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition"
                >
                  📈 Úspěšnost OZ
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showLeadSuccessStatsModal && (
        <LeadSuccessStatsModal
          isDarkMode={isDarkMode}
          currentUserId={currentUserId}
          onClose={() => setShowLeadSuccessStatsModal(false)}
          initialMonth={selectedMonth}
          batchOptions={allBatchNames.map(batchName => ({ batchName }))}
        />
      )}
    </div>
  );
}
